import { Link } from 'react-router-dom';
import { LANDING_STATIC_CONTENT } from '@/domains/landing-page/data/landing-content';
import { formatLandingStat, useLandingData } from '@/domains/landing-page/context/LandingDataContext';

const STAT_CONFIG = [
  { key: 'completed_deliveries' as const, icon: 'ri-box-3-line' },
  { key: 'coverage_areas' as const, icon: 'ri-map-pin-line' },
  { key: 'average_rating' as const, icon: 'ri-star-fill' },
  { key: 'average_delivery_hours' as const, icon: 'ri-time-line' },
];

export default function HeroSection() {
  const { stats, loading } = useLandingData();
  const { hero, statLabels } = LANDING_STATIC_CONTENT;

  return (
    <section className="relative min-h-[92vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-teal-900 via-teal-800 to-teal-700">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-16 left-16 w-64 h-64 rounded-full border-2 border-white" />
        <div className="absolute top-32 left-32 w-32 h-32 rounded-full border border-white" />
        <div className="absolute bottom-24 right-20 w-80 h-80 rounded-full border-2 border-teal-400" />
        <div className="absolute bottom-40 right-40 w-40 h-40 rounded-full border border-teal-300" />
        <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-orange-400 rounded-full opacity-30" />
        <div className="absolute top-1/3 right-1/3 w-8 h-8 bg-white rounded-full opacity-20" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 border border-orange-400/40 rounded-full text-orange-300 text-sm font-medium mb-8 backdrop-blur-sm">
          <i className="ri-award-line" />
          {hero.badge}
        </div>

        <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
          {hero.title}
          <span className="text-orange-400"> {hero.titleHighlight}</span>
          <br />{hero.titleSuffix}
        </h1>

        <p className="text-lg md:text-xl text-teal-100 mb-10 max-w-2xl mx-auto leading-relaxed">
          {hero.subtitle}
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
          <Link
            to="/register/client"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl text-lg transition-all duration-200 shadow-lg shadow-orange-500/30 hover:shadow-xl hover:scale-105"
          >
            <i className="ri-user-add-line" />
            Envoyer un colis
          </Link>
          <Link
            to="/register/livreur"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl text-lg border-2 border-white/30 hover:border-white/50 transition-all duration-200 backdrop-blur-sm"
          >
            <i className="ri-truck-line" />
            Devenir livreur
          </Link>
          <Link
            to="/login"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent text-teal-200 hover:text-white font-semibold rounded-xl text-lg border border-teal-400/40 hover:border-teal-300 transition-all duration-200"
          >
            <i className="ri-login-circle-line" />
            Se connecter
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
          {STAT_CONFIG.map((stat) => (
            <div
              key={stat.key}
              className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-4 text-center"
            >
              <i className={`${stat.icon} text-orange-400 text-2xl mb-1 block`} />
              <p className="text-2xl font-bold text-white">
                {loading ? '…' : formatLandingStat(stat.key, stats)}
              </p>
              <p className="text-xs text-teal-200 mt-0.5">{statLabels[stat.key]}</p>
            </div>
          ))}
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-teal-300 text-2xl" />
        </div>
      </div>
    </section>
  );
}
